import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

// SDK officiel SumUp Payment Widget — la carte est saisie dans l'iframe SumUp,
// les données bancaires ne transitent JAMAIS par MILES (PCI by design).
const SDK_URL = "https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js";
const WIDGET_LOCALE = { fr: "fr-FR", en: "en-GB", de: "de-DE" };

let sdkPromise = null;
function loadSdk() {
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      if (window.SumUpCard) return resolve();
      const script = document.createElement("script");
      script.src = SDK_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("sdk_load_failed"));
      document.head.appendChild(script);
    });
  }
  return sdkPromise;
}

export default function SumUpPayment({ checkoutId, registerPay, onCompleteChange, email }) {
  const { lang, t } = useLang();
  const widgetRef = useRef(null);
  const resolverRef = useRef(null);
  const timeoutRef = useRef(null);
  const sentFallbackRef = useRef(null);
  const [error, setError] = useState("");

  // montage du widget sur le checkout_id fourni par le backend
  useEffect(() => {
    if (!checkoutId) return undefined;
    let cancelled = false;
    setError("");
    loadSdk().then(() => {
      if (cancelled || !window.SumUpCard) return;
      widgetRef.current = window.SumUpCard.mount({
        id: "sumup-card-widget",
        checkoutId,
        showSubmitButton: false, // le CTA MILES pilote submit()
        locale: WIDGET_LOCALE[lang] || "fr-FR",
        email: email || undefined,
        onLoad: () => { if (!cancelled) onCompleteChange(true); },
        onResponse: (type) => {
          // « success » = seul événement terminal fiable. Avec 3DS, « sent » arrive AVANT
          // l'authentification (auth-screen) — résoudre sur « sent » faisait échouer la
          // vérification serveur. Filet de sécurité : si une configuration ne renvoie QUE
          // « sent », on résout après 20 s — la boucle de confirm backend tranche le statut.
          if (type === "sent") {
            clearTimeout(sentFallbackRef.current);
            sentFallbackRef.current = setTimeout(() => {
              const pendingResolve = resolverRef.current;
              if (pendingResolve) { resolverRef.current = null; pendingResolve({ ok: true }); }
            }, 20000);
            return;
          }
          if (type === "auth-screen") return; // 3DS affiché dans le widget — état intermédiaire
          clearTimeout(sentFallbackRef.current);
          const resolve = resolverRef.current;
          if (!resolve) return;
          resolverRef.current = null;
          clearTimeout(timeoutRef.current);
          if (type === "success") resolve({ ok: true });
          else if (type === "invalid") resolve({ error: { message: "card_invalid" } });
          else resolve({ error: { message: "payment_failed" } }); // fail / error
        },
      });
    }).catch(() => { if (!cancelled) setError("sdk_load_failed"); });
    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
      clearTimeout(sentFallbackRef.current);
      resolverRef.current = null;
      try { widgetRef.current?.unmount(); } catch { /* déjà démonté */ }
      widgetRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutId]);

  // le CTA MILES appelle submit() et attend l'événement terminal du widget
  useEffect(() => {
    registerPay(async () => {
      if (!widgetRef.current) return { error: { message: "not_ready" } };
      return new Promise((resolve) => {
        resolverRef.current = resolve;
        // garde-fou : aucune réponse du widget sous 120 s → timeout propre
        timeoutRef.current = setTimeout(() => {
          if (resolverRef.current) {
            resolverRef.current = null;
            resolve({ error: { message: "payment_timeout" } });
          }
        }, 120000);
        try {
          widgetRef.current.submit();
        } catch {
          resolverRef.current = null;
          clearTimeout(timeoutRef.current);
          resolve({ error: { message: "not_ready" } });
        }
      });
    });
  }, [registerPay]);

  if (!checkoutId) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-ink/10 bg-cloud px-5 py-6 text-sm text-slate" data-testid="payment-element-loading">
        <Loader2 className="h-4 w-4 animate-spin text-miles" strokeWidth={1.5} />
        {t("bk.pay_loading")}
      </div>
    );
  }

  return (
    <div data-testid="sumup-payment-widget">
      <div id="sumup-card-widget" />
      {error && (
        <p className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-medium text-red-600" data-testid="sumup-sdk-error">
          {t("bk.pay_err_d")}
        </p>
      )}
    </div>
  );
}
